(function () {
  'use strict';

  if (customElements.get('social-videos')) return;

  customElements.define(
    'social-videos',
    class SocialVideos extends HTMLElement {
      static allSections = [];

      constructor() {
        super();
        this._intersectionObserver = null;
      }

      connectedCallback() {
        SocialVideos.allSections.push(this);
        this.videoItems = this.querySelectorAll('[data-social-video-item]');
        this.videoItems?.forEach((item, index) => this.initVideoItem(item, index));
        this.initIntersectionObserver();
      }

      initVideoItem(item, index) {
        const video = item.querySelector('video');
        const playButton = item.querySelector('[data-video-play]');
        const soundButton = item.querySelector('[data-video-sound]');

        if (!video || !playButton) return;

        item._video = video;
        item._playButton = playButton;
        item._soundButton = soundButton || null;
        item._index = index;

        // Desktop: hover to play/pause
        item.addEventListener('mouseenter', () => this.playVideo(item));
        item.addEventListener('mouseleave', () => this.pauseVideo(item));

        // Play button
        playButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleVideo(item);
        });
        playButton.addEventListener('touchstart', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleVideo(item);
        });

        if (soundButton) {
          soundButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleMute(item);
          });
          soundButton.addEventListener('touchstart', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleMute(item);
          });
        }
      }

      initIntersectionObserver() {
        if (!('IntersectionObserver' in window)) return;

        const isTouchDevice = window.theme?.touch || 'ontouchstart' in window;
        if (!isTouchDevice) return;

        this._intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const item = entry.target;
              if (!item._video) return;
              if (entry.isIntersecting) {
                this.playVideo(item);
              } else {
                this.pauseVideo(item);
              }
            });
          },
          { threshold: 0.5 }
        );

        this.videoItems?.forEach((item) => this._intersectionObserver.observe(item));
      }

      toggleVideo(item) {
        item._video.paused ? this.playVideo(item) : this.pauseVideo(item);
      }

      toggleMute(item) {
        if (!item._video) return;
        item._video.muted = !item._video.muted;
        if (item._video.muted) {
          item.classList.remove('is-unmuted');
          item._soundButton?.setAttribute('aria-label', theme.strings.unmuteVideo || 'Unmute video');
        } else {
          item.classList.add('is-unmuted');
          item._soundButton?.setAttribute('aria-label', theme.strings.muteVideo || 'Mute video');
        }
      }

      playVideo(item) {
        if (!item._video || !item._video.paused) return;

        this.pauseAllOtherVideos(item);

        const promise = item._video.play();
        if (promise !== undefined) {
          promise
            .then(() => {
              item.classList.add('is-playing');
              item._playButton.setAttribute('aria-label', theme.strings.pauseVideo);
            })
            .catch((e) => {
              console.log('Video autoplay prevented:', e);
            });
        }
      }

      pauseVideo(item) {
        if (!item._video || item._video.paused) return;
        item.classList.remove('is-playing');
        item._video.pause();
        item._playButton.setAttribute('aria-label', theme.strings.playVideo);
      }

      pauseAllOtherVideos(target) {
        SocialVideos.allSections.forEach((section) => {
          section.videoItems?.forEach((item) => {
            if (item !== target && item._video && !item._video.paused) {
              this.pauseVideo(item);
            }
          });
        });
      }

      disconnectedCallback() {
        this._intersectionObserver?.disconnect();
        const index = SocialVideos.allSections.indexOf(this);
        if (index > -1) SocialVideos.allSections.splice(index, 1);
      }
    }
  );
})();
